// Replace by… (section 6.1): pick the replacement in the same template family, preview every reference the API knows,
// then stage one edit per referencing document and, when asked, the deletion of the original.
import { useEffect, useState } from 'react';
import { ApiError } from '../../api/client';
import { ops } from '../../api/operations';
import type { Document, DocumentReference } from '../../api/types';
import { Button } from '../../ui/Button';
import { AlertCircle } from '../../ui/icons';
import { Checkbox } from '../../ui/inputs';
import { Modal } from '../../ui/Modal';
import { useToasts } from '../../ui/Toast';
import { baseOf, stageReplaceBy } from '../actions';
import { ReferencePicker } from '../widgets/ReferenceField';
import { useWidgetEnv } from '../widgets/context';

export function ReplaceByDialog({ doc, onClose }: { doc: Document; onClose: () => void }) {
  const { catalog, client, org, game, branch, workspace } = useWidgetEnv();
  const toasts = useToasts();
  const [replacement, setReplacement] = useState<string | null>(null);
  const [references, setReferences] = useState<DocumentReference[] | null>(null);
  const [resolved, setResolved] = useState<{ document: Document; path: string }[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deleteOriginal, setDeleteOriginal] = useState(false);
  const [busy, setBusy] = useState(false);
  const family = catalog.templates.get(doc.template);
  const root = rootOf(catalog.templates, doc.template);

  useEffect(() => {
    if (!replacement) return;
    let alive = true;
    (async () => {
      try {
        const refs = await client.all<DocumentReference>((cursor) => client.data(ops.listDocumentReferences, { path: { org, game, branch, document: doc.id }, query: { limit: 200, cursor } }));
        if (!alive) return;
        setReferences(refs);
        const byId = new Map<string, Document>();
        for (const r of refs) {
          if (byId.has(r.documentId)) continue;
          byId.set(r.documentId, await client.data(ops.getDocument, { path: { org, game, branch, document: r.documentId } }));
        }
        if (!alive) return;
        setResolved(refs.map((r) => ({ document: byId.get(r.documentId)!, path: r.path })));
      } catch (e) {
        if (alive) setError(e instanceof ApiError ? e.message : String(e));
      }
    })();
    return () => {
      alive = false;
    };
  }, [replacement, client, org, game, branch, doc.id]);

  if (!replacement) {
    return <ReferencePicker param={{ template: root, type: 'document', name: 'replacement', displayName: `Replace ${catalog.label(doc)} by` }} selected={[]} onClose={onClose} onPick={(ids) => (ids[0] && ids[0] !== doc.id ? setReplacement(ids[0]) : onClose())} />;
  }
  const target = catalog.resolve(replacement);
  const apply = () => {
    if (!resolved) return;
    setBusy(true);
    const touched = stageReplaceBy(workspace, catalog, resolved, doc.id, replacement);
    if (deleteOriginal) workspace.remove('document', doc.id, baseOf(doc), { label: catalog.label(doc), template: doc.template });
    toasts.success(`${resolved.length} reference${resolved.length === 1 ? '' : 's'} in ${touched} document${touched === 1 ? '' : 's'} staged${deleteOriginal ? '; the original is staged for deletion' : ''}. Save All commits them together.`);
    setBusy(false);
    onClose();
  };
  const documents = new Set((references ?? []).map((r) => r.documentId)).size;
  return (
    <Modal
      title={`Replace ${catalog.label(doc)}`}
      open
      onClose={onClose}
      size="m"
      footer={
        <>
          <span className="spacer" />
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={apply} disabled={!resolved || busy} busy={busy}>
            Stage {references ? `${references.length} change${references.length === 1 ? '' : 's'}` : ''}
          </Button>
        </>
      }
    >
      <div className="stack dialog-form">
        <p>
          Every place using <strong>{catalog.label(doc)}</strong> will point to <strong>{target ? catalog.label(target) : replacement}</strong> instead. {family && family.name !== root ? 'Only documents of the same template family can be chosen.' : ''}
        </p>
        {error && (
          <div className="banner banner-danger" role="alert">
            <AlertCircle className="banner-icon" aria-hidden="true" />
            <div className="banner-body">{error}</div>
          </div>
        )}
        {references === null && !error && (
          <span className="loading-line" role="status">
            <span className="spinner" aria-hidden="true" />
            Counting references…
          </span>
        )}
        {references !== null && references.length === 0 && <p className="muted">This document is not referenced anywhere.</p>}
        {references !== null && references.length > 0 && (
          <>
            <p>
              <strong>
                Will replace {references.length} reference{references.length === 1 ? '' : 's'} in {documents} document{documents === 1 ? '' : 's'}
              </strong>
            </p>
            <ul className="small ref-list">
              {references.slice(0, 50).map((r, i) => (
                <li key={`${r.documentId}-${r.path}-${i}`}>
                  {(() => {
                    const d = catalog.resolve(r.documentId);
                    return d ? catalog.label(d) : r.documentId.slice(0, 8);
                  })()}{' '}
                  <code className="muted">› {r.path}</code>
                </li>
              ))}
              {references.length > 50 && <li className="muted">… and {references.length - 50} more</li>}
            </ul>
          </>
        )}
        <Checkbox label="Delete the original document afterwards (staged)" checked={deleteOriginal} onChange={(e) => setDeleteOriginal(e.target.checked)} />
      </div>
    </Modal>
  );
}

/** The topmost ancestor of a template in the family a reference may point at: the template itself when it has no base of the same kind. */
function rootOf(templates: Map<string, { name: string; parent?: string | null; kind: string }>, name: string): string {
  let current = templates.get(name);
  const seen = new Set<string>();
  while (current?.parent && !seen.has(current.parent)) {
    const parent = templates.get(current.parent);
    if (!parent || parent.kind !== current.kind || parent.name.startsWith('SmartObjects.') || parent.name.startsWith('LiveOps.')) break;
    seen.add(current.name);
    current = parent;
  }
  return current?.name ?? name;
}
